import React, { useState } from 'react';
import styled from 'styled-components';
import { TextField, InputAdornment, IconButton } from '@mui/material';
import { Search, Tune, Clear } from '@mui/icons-material';
import { colors, borderRadius, spacing, typography, shadows } from '../styles/theme';

const SearchContainer = styled.div`
  position: relative;
  margin-bottom: ${spacing.lg};
  width: 100%;
`;

const StyledTextField = styled(TextField)`
  width: 100%;

  & .MuiOutlinedInput-root {
    background: ${colors.backgroundSecondary};
    border-radius: ${borderRadius.lg};
    transition: all 0.3s ease;

    &:hover {
      background: ${colors.backgroundTertiary};
    }

    &.Mui-focused {
      background: ${colors.backgroundSecondary};
      box-shadow: ${shadows.md};
    }

    & fieldset {
      border: 1px solid ${colors.borderLight};
      transition: border-color 0.3s ease;
    }

    &:hover fieldset {
      border-color: ${colors.primary};
    }

    &.Mui-focused fieldset {
      border-color: ${colors.primary};
    }

    & .MuiOutlinedInput-input {
      padding: ${spacing.md} ${spacing.md};
      font-family: ${typography.fontFamily.primary};
      font-size: ${typography.fontSize.md};
      color: ${colors.textPrimary};
      font-weight: ${typography.fontWeight.regular};

      &::placeholder {
        color: ${colors.textTertiary};
        opacity: 1;
      }
    }
  }

  & .MuiInputLabel-root {
    color: ${colors.textSecondary};
    font-family: ${typography.fontFamily.primary};
    font-weight: ${typography.fontWeight.medium};
  }
`;


interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onFilterClick?: () => void;
  showFilter?: boolean;
}

const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  placeholder = "Поиск блюд...",
  onFilterClick,
  showFilter = true
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <SearchContainer>
      <StyledTextField
        variant="outlined"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search sx={{
                color: isFocused ? colors.primary : colors.textTertiary,
                fontSize: 20,
                transition: 'color 0.3s ease'
              }} />
            </InputAdornment>
          ),
          endAdornment: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              {value && (
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{
                    color: colors.textTertiary,
                    '&:hover': {
                      color: colors.primary
                    }
                  }}
                >
                  <Clear fontSize="small" />
                </IconButton>
              )}
              {showFilter && onFilterClick && (
                <IconButton
                  size="small"
                  onClick={onFilterClick}
                  sx={{
                    background: colors.primary,
                    color: colors.textInverse,
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    '&:hover': {
                      background: colors.primaryDark,
                    }
                  }}
                >
                  <Tune fontSize="small" />
                </IconButton>
              )}
            </div>
          ),
        }}
      />
    </SearchContainer>
  );
};

export default SearchBar;
